/**
 * HubSpot form embeds. See the `render_block_core/html` filter in
 * `inc/filters.php`.
 *
 * The server changes the embed scripts to `type="text/plain"`. Thus the
 * browser does not load the HubSpot form library during the page load.
 * This script restores the embed scripts when the form comes near the
 * viewport. The scripts run in their initial sequence and position.
 * HubSpot puts the form after the script that calls
 * `hbspt.forms.create()` (`document.currentScript`).
 */

/**
 * The load of each external script, by URL.
 * @type {Map<string, Promise<void>>}
 */
const libraries = new Map();

/**
 * Replaces a placeholder with a script that runs.
 * @param {HTMLScriptElement} placeholder
 * @returns {Promise<void>} Resolves when an external script has loaded.
 */
const restore = (placeholder) => {
  const { src } = placeholder;

  // A page can have more than one form. Load each library one time only.
  if (src && libraries.has(src)) {
    placeholder.remove();
    return libraries.get(src);
  }

  const script = document.createElement("script");
  for (const { name, value } of placeholder.attributes) {
    if (name !== "type" && name !== "data-hubspot-embed") {
      script.setAttribute(name, value);
    }
  }
  script.text = placeholder.text;

  let loaded = Promise.resolve();
  if (src) {
    // Continue after an error too. Then the form code fails as it did before.
    loaded = new Promise((resolve) => {
      script.addEventListener("load", () => resolve());
      script.addEventListener("error", () => resolve());
    });
    libraries.set(src, loaded);
  }

  placeholder.replaceWith(script);
  return loaded;
};

/**
 * Restores the scripts of one embed, one after the other.
 * @param {HTMLScriptElement[]} placeholders
 */
const embed = async (placeholders) => {
  for (const placeholder of placeholders) {
    await restore(placeholder);
  }
};

// The scripts of an embed have the same parent. The parent shows on the
// page, but a script does not. Thus, observe the parent.
/** @type {Map<Element, HTMLScriptElement[]>} */
const embeds = new Map();
for (const placeholder of $$("script[type='text/plain'][data-hubspot-embed]")) {
  const parent = placeholder.parentElement;
  embeds.set(parent, [ ...(embeds.get(parent) || []), placeholder ]);
}

if (embeds.size && "IntersectionObserver" in window) {
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      observer.unobserve(entry.target);
      embed(embeds.get(entry.target));
    }
  }, {
    // The library and the form need time to load.
    // Start when the form is one viewport height away.
    rootMargin: "100% 0px",
  });

  for (const parent of embeds.keys()) {
    observer.observe(parent);
  }
} else {
  for (const placeholders of embeds.values()) {
    embed(placeholders);
  }
}
